import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import LogoDisplay from "../../config/LogoDisplay";

export default function LoginPage() {
  const { user, signInWithEmail, signInWithGoogle, resetPassword } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/dashboard";

  const [form, setForm] = useState({ email: "", password: "" });
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Forward to the destination once authentication completes. This is the
  // single source of truth for post-login navigation and must NOT live inside
  // the click handlers: the Google *redirect* flow (used as a fallback when the
  // popup is blocked, on any platform) does a full-page round-trip to Google
  // and returns to /login on a fresh load — the code after `await
  // signInWithGoogle()` never runs, and `getRedirectResult` in AuthContext
  // signs the user in out-of-band. Watching `user` catches that path, the popup
  // path (no race with onAuthStateChanged), and already-signed-in users who
  // land here. `replace` keeps the auth page out of history.
  useEffect(() => {
    if (user) nav(redirect, { replace: true });
  }, [user, redirect, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await signInWithEmail(form.email.trim(), form.password);
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    setErr("");
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      setErr(prettify(e));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await resetPassword((resetEmail || form.email).trim());
      setResetSent(true);
    } catch (e) {
      setErr(prettify(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0B1038] flex flex-col font-sans">
      <div className="px-6 py-5">
        <LogoDisplay size="md" showWordmark={false} />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-md p-6 sm:p-8 shadow-sm">
          <h1 className="font-display text-3xl text-[#0B1038] tracking-tight leading-tight">
            Welcome back.
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Log in to continue to <span className="text-blue-600 font-medium">Collavue</span>.
          </p>

          {err && (
            <div className="mt-4 px-3 py-2 rounded-sm bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              {err}
            </div>
          )}

          {!showReset ? (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <Input
                Icon={Mail}
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                autoComplete="email"
              />
              <Input
                Icon={Lock}
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="current-password"
              />
              <button
                disabled={loading}
                className="w-full inline-flex justify-center items-center gap-2 bg-[#0B1038] text-white font-semibold py-2.5 rounded-sm hover:bg-[#151c54] disabled:opacity-60 transition-colors"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Log in
              </button>
              <button
                type="button"
                onClick={google}
                disabled={loading}
                className="w-full text-sm font-semibold py-2.5 rounded-sm border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Continue with Google
              </button>
              <button
                type="button"
                onClick={() => setShowReset(true)}
                className="text-xs text-blue-600 hover:underline"
              >
                Forgot password?
              </button>
            </form>
          ) : (
            <form onSubmit={sendReset} className="mt-6 space-y-3">
              <p className="text-sm text-gray-600">
                Enter your email and we'll send a reset link.
              </p>
              <Input
                Icon={Mail}
                type="email"
                placeholder="Email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              {resetSent && (
                <div className="text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-sm text-sm">
                  Check your inbox for a reset link.
                </div>
              )}
              <button className="w-full bg-[#0B1038] text-white font-semibold py-2.5 rounded-sm hover:bg-[#151c54]">
                Send reset link
              </button>
              <button
                type="button"
                onClick={() => setShowReset(false)}
                className="text-xs text-gray-500 hover:text-[#0B1038]"
              >
                Back to login
              </button>
            </form>
          )}

          <div className="mt-6 text-sm text-gray-600 text-center">
            Don't have an account?{" "}
            <Link to="/signup" className="font-semibold text-blue-600 hover:underline">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ Icon, ...props }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      )}
      <input
        {...props}
        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-shadow"
      />
    </div>
  );
}

function prettify(err) {
  if (!err) return "Something went wrong.";
  if (err.code) {
    return err.code
      .replace("auth/", "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return err.message || "Something went wrong.";
}
